import { forwardRef, type Ref, type ReactNode, type InputHTMLAttributes } from 'react';
import { useStableId, useUncontrolled } from '@repletoryreach/sdk-utils/hooks';
import './Slider.css';

export interface SliderProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'color' | 'onChange' | 'value' | 'type' | 'defaultValue'> {
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: ReactNode;
  description?: ReactNode;
  showValue?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

/** Range slider: native `<input type="range">` themed. */
export const Slider = forwardRef(function Slider(
  { value, defaultValue, onChange, min = 0, max = 100, step = 1, label, description, showValue, size = 'sm', id, ...inputProps }: SliderProps,
  ref: Ref<HTMLInputElement>
): ReactNode {
  const [val, setVal] = useUncontrolled<number>({
    value, defaultValue, finalValue: min, onChange
  });
  const auto = useStableId('rr-slider');
  const inputId = id ?? auto;
  const percent = ((val - min) / (max - min)) * 100;
  return (
    <div className={`rr-slider rr-slider--size-${size}`}>
      {label || showValue ? (
        <div className="rr-slider__head">
          {label ? <label htmlFor={inputId} className="rr-slider__label">{label}</label> : <span />}
          {showValue ? <span className="rr-slider__value">{val}</span> : null}
        </div>
      ) : null}
      <input
        ref={ref}
        id={inputId}
        type="range"
        min={min}
        max={max}
        step={step}
        value={val}
        onChange={(e) => setVal(Number(e.currentTarget.value))}
        style={{ ['--rr-slider-fill' as string]: `${percent}%` }}
        {...inputProps}
      />
      {description ? <div className="rr-slider__description">{description}</div> : null}
    </div>
  );
});
